import { VariantType } from '@/components/sections/builder/elements/body/product-detail/variant-picker';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useCartProduct } from '@/stores/cart';
import { IProduct, ProductItem } from '@/types/product';
import { shallow } from 'zustand/shallow';

type DropdownPickerProps = {
  variants: VariantType[];
  product: IProduct;
  items: ProductItem[];
  selectedOptions: Record<string, string>;
  handleSelect: (variantId: string, optionId: string) => void;
};

export const DropdownPicker = ({
  variants,
  product,
  items,
  selectedOptions,
  handleSelect,
}: DropdownPickerProps) => {
  const cartStore = useCartProduct(
    (state) => ({
      setQuantity: state.setQuantity,
    }),
    shallow
  );
  const availableQty = product.available_qty as number;

  return (
    <div className='w-full space-y-4 '>
      {variants.map((variant) => (
        <div key={variant.variant_id} className='space-y-2'>
          <label className='mb-1 block text-sm font-medium text-gray-700'>{variant.label}</label>
          <Select
            defaultValue='-'
            value={selectedOptions[variant.variant_id] || '-'}
            onValueChange={(value) => {
              if (availableQty === 0) return;
              handleSelect(variant.variant_id, value === '-' ? '' : value);
              cartStore.setQuantity(1);
            }}
          >
            <SelectTrigger
              className='mb-4 mt-3 h-auto min-h-0 w-full items-start break-all py-2 text-left'
              style={{
                ['--radix-select-trigger-height' as any]: 'auto',
              }}
            >
              <SelectValue
                placeholder={`Choose ${variant.label}`}
                className='whitespace-normal leading-snug'
              />
            </SelectTrigger>

            <SelectContent position='popper' className='w-[--radix-select-trigger-width]'>
              <ScrollArea className='max-h-60 overflow-y-auto'>
                <SelectItem className='' value='-'>
                  Choose {variant.label}
                </SelectItem>
                {variant.options.map((option) => {
                  const isSelected = selectedOptions[variant.variant_id] === option.option_id;

                  const tempOptions = { ...selectedOptions };
                  if (isSelected) {
                    delete tempOptions[variant.variant_id];
                  } else {
                    tempOptions[variant.variant_id] = option.option_id;
                  }

                  const tempSelectedOption = Object.fromEntries(
                    Object.entries(tempOptions).filter(([, v]) => v)
                  );

                  const tempAllSelected =
                    Object.keys(tempSelectedOption).length === variants.length;

                  let isOutOfStock = false;
                  if (tempAllSelected) {
                    const selectedKey = variants
                      .map((v) => tempSelectedOption[v.variant_id])
                      .filter(Boolean)
                      .join(':');
                    const stock = items.find((item) => item.item_option_id === selectedKey)?.stock;
                    isOutOfStock = stock === undefined || stock === 0;
                  }

                  const isOutOfStockForCombination = tempAllSelected
                    ? isOutOfStock && !isSelected
                    : false;
                  const disabled = availableQty === 0 || isOutOfStockForCombination;

                  return (
                    <SelectItem
                      key={option.option_id}
                      value={option.option_id}
                      disabled={disabled}
                      className={cn(
                        '  break-all',
                        disabled && 'cursor-not-allowed text-gray-400 line-through'
                      )}
                    >
                      {option.value}
                    </SelectItem>
                  );
                })}
              </ScrollArea>
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
};
